import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { TICKETS_FOR_OE_ERROR_URL } from 'src/constants/AppConstants';
import { useNotification } from './NotificationProvider';
import { FiltersStatus } from './SharedFiltersProvider';
import { FinTechOpsCategories, FinTechOpsMessages, InitialSasTabData } from '../../FinTechOpsConstants';
import { SaSDashboardItems } from '../../fintech-ops-dashboard/FinTechOpsDataInterfaces';
import { SasParsedDetails } from '../../sas/SasInterfaces';
import { useSasData } from '../hooks/useSasData';

export interface SasContextValue {
  finTechOpsSasDataStatus: FiltersStatus;
  setFinTechOpsSasDataStatus: (s: FiltersStatus) => void;
  sasData: SasParsedDetails;
  setSasData: (d: SasParsedDetails) => void;
  finTechOpsSasDashBoard: SaSDashboardItems;
  setFinTechOpsSasDashBoard: (d: SaSDashboardItems) => void;
}

const EMPTY_PARSED = {} as SasParsedDetails;

const defaultValue: SasContextValue = {
  finTechOpsSasDataStatus: 'loading',
  setFinTechOpsSasDataStatus: () => {},
  sasData: EMPTY_PARSED,
  setSasData: () => {},
  finTechOpsSasDashBoard: InitialSasTabData,
  setFinTechOpsSasDashBoard: () => {}
};

const SasContext = createContext<SasContextValue>(defaultValue);

export const useSas = () => useContext(SasContext);

interface Props {
  children: React.ReactNode;
}

export const SasProvider = ({ children }: Props) => {
  const { data, loading, error } = useSasData();
  const { displayNotification } = useNotification();
  const [parsed, setParsed] = useState<SasParsedDetails>(EMPTY_PARSED);
  const [dashboard, setDashboard] = useState<SaSDashboardItems>(InitialSasTabData);
  const [status, setStatus] = useState<FiltersStatus>('loading');

  useEffect(() => {
    if (loading) {
      setStatus('loading');
      return;
    }
    if (error) {
      logger.error(`Unable to load ${FinTechOpsCategories.sas} data`, error);
      setStatus('error');
      displayNotification('error', FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
      return;
    }
    if (data) {
      setParsed(data);
      setStatus('finished');
    }
  }, [loading, error, data, displayNotification]);

  const setFinTechOpsSasDataStatus = useCallback((s: FiltersStatus) => setStatus(s), []);
  const setSasData = useCallback((d: SasParsedDetails) => setParsed(d), []);
  const setFinTechOpsSasDashBoard = useCallback((d: SaSDashboardItems) => setDashboard(d), []);

  const value = useMemo<SasContextValue>(
    () => ({
      finTechOpsSasDataStatus: status,
      setFinTechOpsSasDataStatus,
      sasData: parsed,
      setSasData,
      finTechOpsSasDashBoard: dashboard,
      setFinTechOpsSasDashBoard
    }),
    [status, parsed, dashboard, setFinTechOpsSasDataStatus, setSasData, setFinTechOpsSasDashBoard]
  );

  return <SasContext.Provider value={value}>{children}</SasContext.Provider>;
};
